import { CommonModule, DatePipe } from '@angular/common';
import { Component, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { I18nService } from '../core/i18n.service';
import { SessionService } from '../core/session.service';
import { TeamsApiService } from '../core/teams-api.service';
import { TeamInvitationLinkAccessPayload } from '../core/teams-api.types';

@Component({
  selector: 'app-team-invitation-link-page',
  standalone: true,
  imports: [CommonModule, RouterLink, DatePipe],
  template: `
    <section class="page">
      <h1>{{ t('teamInvite.title') }}</h1>

      @if (loading()) {
        <p class="state">{{ t('teamInvite.loading') }}</p>
      } @else if (errorMessage()) {
        <p class="state state--error">{{ errorMessage() }}</p>
      } @else if (invitation(); as invite) {
        <article class="card">
          <h2>{{ invite.team_name }}</h2>
          <p class="meta">{{ t('teamInvite.role', { value: invite.invited_role }) }}</p>
          <p class="meta">{{ t('teamInvite.expires', { value: (invite.expires_at | date: 'short') ?? '' }) }}</p>

          @if (!session.isAuthenticated()) {
            <p class="state">{{ t('teamInvite.authRequired') }}</p>
            <div class="actions">
              <a class="action-link" [routerLink]="['/player/login']" [queryParams]="{ returnTo: currentUrl() }">{{ t('teamInvite.playerLogin') }}</a>
              <a class="action-link" [routerLink]="['/player/register']" [queryParams]="{ returnTo: currentUrl() }">{{ t('teamInvite.playerRegister') }}</a>
            </div>
          } @else if (invite.can_accept) {
            @if (successMessage()) {
              <p class="state state--success">{{ successMessage() }}</p>
            }
            @if (actionError()) {
              <p class="state state--error">{{ actionError() }}</p>
            }

            <button type="button" [disabled]="accepting()" (click)="acceptInvitation()">
              {{ accepting() ? t('teamInvite.accepting') : t('teamInvite.accept') }}
            </button>
          } @else {
            <p class="state">{{ statusMessage(invite) }}</p>
          }
        </article>
      }
    </section>
  `,
  styles: `
    .page { padding: 1rem; max-width: 680px; margin: 0 auto; }
    h1 { margin: 0 0 0.8rem; font-size: 1.45rem; color: var(--text-strong); }
    .card { border: 1px solid var(--line-soft); border-radius: 12px; padding: 0.9rem; background: linear-gradient(180deg, rgba(23, 36, 58, 0.94) 0%, rgba(18, 29, 47, 0.94) 100%); box-shadow: var(--shadow-soft); }
    h2 { margin: 0 0 0.4rem; color: var(--text-strong); font-size: 1.2rem; }
    .meta { margin: 0.2rem 0; color: var(--text-muted); }
    .state { margin: 0.65rem 0; color: var(--text-muted); }
    .state--error { color: var(--danger); }
    .state--success { color: #2b8f4b; }
    .actions { display: flex; gap: 0.6rem; flex-wrap: wrap; margin-top: 0.75rem; }
    .action-link { border: 1px solid rgba(138, 184, 255, 0.6); border-radius: 8px; padding: 0.5rem 0.75rem; text-decoration: none; color: #091325; background: linear-gradient(180deg, var(--accent) 0%, var(--accent-strong) 100%); font-weight: 700; }
    button { margin-top: 0.7rem; border: 1px solid rgba(138, 184, 255, 0.6); border-radius: 8px; padding: 0.55rem 0.8rem; background: linear-gradient(180deg, var(--accent) 0%, var(--accent-strong) 100%); color: #091325; font-weight: 700; }
  `
})
export class TeamInvitationLinkPageComponent {
  private readonly route = inject(ActivatedRoute);
  private readonly teamsApi = inject(TeamsApiService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly i18n = inject(I18nService);
  protected readonly session = inject(SessionService);

  protected readonly invitation = signal<TeamInvitationLinkAccessPayload | null>(null);
  protected readonly loading = signal(true);
  protected readonly accepting = signal(false);
  protected readonly successMessage = signal<string | null>(null);
  protected readonly errorMessage = signal<string | null>(null);
  protected readonly actionError = signal<string | null>(null);
  protected readonly token = signal<string | null>(null);

  constructor() {
    this.route.paramMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      const token = params.get('token');
      this.token.set(token);
      if (!token) {
        this.errorMessage.set(this.t('teamInvite.invalid'));
        this.loading.set(false);
        return;
      }

      this.fetchInvitation(token);
    });
  }

  protected currentUrl(): string {
    const token = this.token();
    return token ? `/teams/invite/${token}` : '/tournaments';
  }

  protected acceptInvitation(): void {
    const token = this.token();
    if (!token) {
      return;
    }

    this.accepting.set(true);
    this.actionError.set(null);
    this.successMessage.set(null);

    this.teamsApi
      .acceptInvitationByToken(token)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ team }) => {
          this.successMessage.set(this.t('teamInvite.accepted'));
          this.accepting.set(false);
          void this.router.navigateByUrl(`/player/teams/${team.id}`);
        },
        error: (error) => {
          const apiError = error?.error?.error;
          const apiErrors = error?.error?.errors;
          this.actionError.set(
            (typeof apiError === 'string' && apiError) ||
              (Array.isArray(apiErrors) && apiErrors.length > 0
                ? apiErrors.join(' | ')
                : this.t('teamInvite.error'))
          );
          this.accepting.set(false);
          this.fetchInvitation(token);
        }
      });
  }

  protected statusMessage(invite: TeamInvitationLinkAccessPayload): string {
    switch (invite.reason) {
      case 'expired':
        return this.t('teamInvite.reason.expired');
      case 'already_member':
        return this.t('teamInvite.reason.alreadyMember');
      case 'already_consumed':
        return this.t('teamInvite.reason.consumed');
      case 'team_full':
        return this.t('teamInvite.reason.full');
      case 'player_account_required':
        return this.t('teamInvite.reason.playerRequired');
      default:
        return this.t('teamInvite.reason.unavailable');
    }
  }

  private fetchInvitation(token: string): void {
    this.loading.set(true);
    this.errorMessage.set(null);

    this.teamsApi
      .getInvitationLinkAccess(token)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ invitation }) => {
          this.invitation.set(invitation);
          this.loading.set(false);
        },
        error: (error) => {
          const status = error?.status;
          if (status === 404) {
            this.errorMessage.set(this.t('teamInvite.invalid'));
          } else {
            this.errorMessage.set(this.t('teamInvite.loadError'));
          }
          this.loading.set(false);
        }
      });
  }
  protected t(key: string, params?: Record<string, string | number | null | undefined>): string {
    return this.i18n.t(key, params);
  }
}
